import { type CreateHotelRoomRequest, type HotelRoomOut } from '@utazzo/shared';
import {
  UzButton,
  UzDrawer,
  UzForm,
  UzFormField,
  UzInput,
  UzNumberInput,
  UzSwitch,
  UzTextarea,
} from '@utazzo/ui';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { RoomTypePicker } from '@/components/pickers';

/**
 * Create/edit dialog for one hotel room.
 *
 * The room owns its catalog identity only - name, category, occupancy. All
 * pricing (per-pax rates, extra beds, meal supplements) lives on the room's
 * price periods (date ranges), edited on the Rooms & rates tab, so it can
 * vary by season.
 */

interface RoomFormValues {
  name: string;
  roomTypeId: string | null;
  description: string | null;
  minOccupancy: number;
  maxOccupancy: number;
  roomInventoryCount: number;
  isActive: boolean;
}

const ROOM_DEFAULTS: RoomFormValues = {
  name: '',
  roomTypeId: null,
  description: null,
  minOccupancy: 1,
  maxOccupancy: 2,
  roomInventoryCount: 0,
  isActive: true,
};

interface RoomFormDialogProps {
  mode: 'create' | 'edit';
  room?: HotelRoomOut;
  onClose: () => void;
  onSubmit: (values: CreateHotelRoomRequest) => Promise<void>;
  pending: boolean;
}

export const RoomFormDialog = ({
  mode,
  room,
  onClose,
  onSubmit,
  pending,
}: RoomFormDialogProps): React.JSX.Element => {
  const form = useForm<RoomFormValues>({ defaultValues: ROOM_DEFAULTS });

  useEffect(() => {
    if (mode === 'create') {
      form.reset(ROOM_DEFAULTS);
      return;
    }
    if (room) {
      form.reset({
        name: room.name,
        roomTypeId: room.roomTypeId,
        description: room.description,
        minOccupancy: room.minOccupancy,
        maxOccupancy: room.maxOccupancy,
        roomInventoryCount: room.roomInventoryCount,
        isActive: room.isActive,
      });
    }
  }, [mode, room, form]);

  const submit = form.handleSubmit(async (values) => {
    await onSubmit({
      name: values.name,
      roomTypeId: values.roomTypeId,
      description: values.description,
      minOccupancy: values.minOccupancy,
      maxOccupancy: values.maxOccupancy,
      roomInventoryCount: values.roomInventoryCount,
      isActive: values.isActive,
    });
  });

  return (
    <UzDrawer
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title={mode === 'create' ? 'Add room type' : `Edit ${room?.name ?? 'room'}`}
      description="Identity and occupancy. Prices, extra beds and meals are added per date range on the room afterwards."
      footer={
        <div className="flex justify-end gap-2">
          <UzButton variant="ghost" onClick={onClose} disabled={pending}>
            Cancel
          </UzButton>
          <UzButton onClick={submit} loading={pending}>
            {mode === 'create' ? 'Add room' : 'Save changes'}
          </UzButton>
        </div>
      }
    >
      <UzForm form={form} onSubmit={submit}>
        <div className="space-y-5">
          <UzFormField name="name">
            {({ field, error }) => (
              <UzInput
                {...field}
                value={(field.value as string | undefined) ?? ''}
                label="Room name"
                placeholder="e.g. Deluxe Room, One Bedroom Pool Villa"
                error={error}
                required
                disabled={pending}
              />
            )}
          </UzFormField>

          <UzFormField name="roomTypeId">
            {({ field, error }) => (
              <RoomTypePicker
                label="Room category"
                value={(field.value as string | null | undefined) ?? null}
                onValueChange={(v) => field.onChange(v)}
                disabled={pending}
                error={error}
              />
            )}
          </UzFormField>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <UzFormField name="minOccupancy">
              {({ field, error }) => (
                <UzNumberInput
                  name={field.name}
                  label="Min occupancy"
                  precision={0}
                  min={1}
                  value={
                    field.value === null || field.value === undefined ? 1 : (field.value as number)
                  }
                  onValueChange={(v) => field.onChange(v ?? 1)}
                  error={error}
                  disabled={pending}
                />
              )}
            </UzFormField>
            <UzFormField name="maxOccupancy">
              {({ field, error }) => (
                <UzNumberInput
                  name={field.name}
                  label="Max occupancy"
                  precision={0}
                  min={1}
                  value={
                    field.value === null || field.value === undefined ? 2 : (field.value as number)
                  }
                  onValueChange={(v) => field.onChange(v ?? 2)}
                  error={error}
                  disabled={pending}
                />
              )}
            </UzFormField>
          </div>

          <UzFormField name="roomInventoryCount">
            {({ field, error }) => (
              <UzNumberInput
                name={field.name}
                label="Rooms available"
                precision={0}
                min={0}
                value={
                  field.value === null || field.value === undefined ? 0 : (field.value as number)
                }
                onValueChange={(v) => field.onChange(v ?? 0)}
                hint="How many physical rooms of this type the hotel has. 0 = no cap."
                error={error}
                disabled={pending}
              />
            )}
          </UzFormField>

          <UzFormField name="description">
            {({ field, error }) => (
              <UzTextarea
                {...field}
                value={(field.value as string | null | undefined) ?? ''}
                onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
                label="Description"
                placeholder="View, amenities, what's included..."
                rows={3}
                error={error}
                disabled={pending}
              />
            )}
          </UzFormField>

          <UzFormField name="isActive">
            {({ field }) => (
              <UzSwitch
                label="Active"
                description="Inactive rooms are hidden from the itinerary picker."
                checked={field.value !== false}
                onCheckedChange={field.onChange}
                disabled={pending}
              />
            )}
          </UzFormField>
        </div>
      </UzForm>
    </UzDrawer>
  );
};
